import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type ReactNode, type RefObject } from 'react';
import { createPortal } from 'react-dom';
import { useAnchoredPopover } from './useAnchoredPopover';

export interface PickOption {
  value: string;
  label: string;
  /** A quiet second line, e.g. a sprint's dates. */
  sub?: string;
  /** A group heading this option sits under, e.g. "Assistants". */
  group?: string;
  /**
   * A small leading glyph, drawn before the label on both the option row and the
   * closed trigger — the board's own status dot or priority chevron, so the menu
   * reads like the board (Vinnie, 17 Oct). Decorative: give it aria-hidden, since
   * the label beside it is the announced text.
   */
  mark?: ReactNode;
  /**
   * Render this option's LABEL as a coloured chip instead of plain text, on both
   * the option row and the closed trigger — the value should read exactly as it
   * does on the card face (Release necessity wears the card's own `.moscow-chip`).
   * The value is the chip's modifier class, e.g. "moscow-chip is-must".
   */
  chipClass?: string;
  /**
   * A small element drawn right after the label, on the option row and the closed
   * trigger — the vendor name-tag (B-07), so a supplier reads as one in the picker
   * the same as everywhere else. The caller builds it, so this component stays free
   * of any people/profile knowledge.
   */
  trailing?: ReactNode;
}

interface Props {
  value: string;
  options: PickOption[];
  ariaLabel: string;
  disabled?: boolean;
  onChange: (value: string) => void;
  /**
   * An element whose top/bottom bound the menu's vertical range instead of the
   * window — pass the card's scroll area (`.modal-body`) for a trigger that sits
   * low in a modal, so the menu flips up to fit the CARD rather than running down
   * the screen. Omit for the normal window-bound behaviour.
   */
  boundaryRef?: RefObject<HTMLElement | null>;
  /**
   * Open the menu the moment the control mounts — the Grid's in-cell editor
   * mounts this already open, so one click (or Enter on the cell) goes straight
   * to the list. Omit for the card, where the trigger opens on its own click.
   */
  autoOpen?: boolean;
  /**
   * Called when the menu closes, with how it closed — the Grid uses it to leave
   * edit mode and move the focused cell (a pick advances down, Tab moves across,
   * Escape stays put). When this is given the control does NOT pull focus back to
   * its own trigger on close, so the grid can place focus on the cell instead.
   * Omit on the card, where closing should restore the trigger as it always has.
   */
  onClose?: (reason: 'pick' | 'cancel' | 'away' | 'next' | 'prev') => void;
  /**
   * A row to pre-HIGHLIGHT when the menu opens on an empty value — the likely
   * pick, keyboard-active so one Enter chooses it, but NOT shown as the chosen
   * value (no check): it is a suggestion, not a commitment. The doer-capture
   * prompt uses it to put the Accountable person forward as the likely doer.
   * Ignored once a real value is set, which always wins the opening highlight.
   */
  suggest?: string;
}

/**
 * THE CARD'S CUSTOM SELECT — the one dropdown every pick-one field on a card now
 * wears (Status, Priority, Release necessity, Type, Phase, Size, Sprint, Assignee),
 * in place of the browser's native <select>, so the whole card reads as one set of
 * controls (Vinnie, 17 Oct: "everything uniform, not native menus or pickers").
 *
 * Built from the same parts as Area, Epic and the date field: the shared
 * .ctl.quiet.area-trigger (the value plus the same chevron) opens the shared
 * .sr-ta popover, which is portaled to <body> and placed by useAnchoredPopover —
 * so it is pinned to the field, flips and shifts to fit the card, never a native
 * OS menu, never a window jump, never clipped. The chosen row carries the brand
 * check (no colour — these are plain values). Keyboard-first like a native select:
 * type to jump to a row, up/down to move, Enter to choose, Escape to close.
 */
export default function PickField({ value, options, ariaLabel, disabled = false, onChange, boundaryRef, autoOpen = false, onClose, suggest }: Props) {
  const [open, setOpen] = useState(autoOpen);
  const [active, setActive] = useState(0);
  const wrap = useRef<HTMLSpanElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const pop = useRef<HTMLDivElement>(null);
  const typeBuf = useRef('');
  const typeAt = useRef(0);
  // How the menu last closed, read by the close-detection effect below so the
  // grid can act on it. Each close path sets this just before setOpen(false).
  const reason = useRef<'pick' | 'cancel' | 'away' | 'next' | 'prev'>('away');
  const wasOpen = useRef(open);
  // Fire onClose exactly once per open→closed transition, whatever closed it
  // (pick, Escape, click-away, Tab). Kept out of each close path so no route can
  // forget it.
  useEffect(() => {
    if (wasOpen.current && !open) onClose?.(reason.current);
    wasOpen.current = open;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);
  // Restore focus to our own trigger ONLY on the card (no onClose). In the grid
  // the trigger is inside a cell that is about to unmount, so the grid places
  // focus itself.
  const refocusTrigger = () => {
    if (!onClose) trigger.current?.focus({ preventScroll: true });
  };
  const pos = useAnchoredPopover(open, trigger, pop, boundaryRef);
  // The popover is rendered visibility:hidden until useAnchoredPopover has
  // measured its position; only then can it take focus. So `visible` — not merely
  // `open` — is what gates the focus move below: focusing while still hidden is
  // silently dropped by the browser and would leave the keyboard dead on the
  // trigger (interaction witness, 17 Oct).
  const visible = open && !!pos;

  const current = options.find((o) => o.value === value);
  const chosenIdx = options.findIndex((o) => o.value === value);

  // Open on the chosen row — or, when nothing real is chosen yet (empty value,
  // which lands on the Unassigned row at index 0), on the SUGGESTED row instead,
  // so the likely pick is under the keyboard without being shown as committed.
  const suggestIdx = suggest ? options.findIndex((o) => o.value === suggest) : -1;
  useEffect(() => {
    if (!open) return;
    const startIdx = chosenIdx > 0 ? chosenIdx : suggestIdx >= 0 ? suggestIdx : chosenIdx >= 0 ? chosenIdx : 0;
    setActive(startIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Move the keyboard into the list once the popover is actually visible — type
  // to jump, arrows to move, Enter to choose all depend on the list holding focus.
  useEffect(() => {
    if (!visible) return;
    pop.current?.focus({ preventScroll: true });
  }, [visible]);

  // Keep the active row in view when it moves (long lists cap to the window).
  useEffect(() => {
    if (!open) return;
    pop.current?.querySelector('.sr-ta-opt.is-active')?.scrollIntoView({ block: 'nearest' });
  }, [active, open]);

  // Click-away and Escape — document level, the app-standard popover rule (the
  // same one ColourField follows). The popover is portaled to <body>, OUTSIDE the
  // card and its focus trap, so a click inside it must be let through (check the
  // trigger and the popover), and Escape must be caught HERE: the card closes on
  // Escape through a window-level (bubble) listener, so a portaled menu's own
  // React handler never beats it — the whole card would drop out of edit instead
  // of the menu closing. Catching it in the document CAPTURE phase and stopping
  // it there gives the open menu first refusal, whatever holds focus.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (trigger.current?.contains(t) || pop.current?.contains(t)) return;
      reason.current = 'away';
      setOpen(false);
    };
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        reason.current = 'cancel';
        setOpen(false);
        refocusTrigger();
      }
    };
    // Close when focus leaves the control entirely — e.g. Tab-away. Without this
    // the menu lingers open but out of focus, and the next Escape (meant to back
    // the card out) closes that stale menu and yanks focus back here instead
    // (interaction witness, 17 Oct — reproduced: open menu → Tab → Escape).
    // Deferred to the next tick so focus can SETTLE first: in WebKit a <button>
    // does not take focus on click, so an option click must not be mistaken for
    // leaving (it keeps focus in the popover, so activeElement stays inside).
    const onFocusOut = () => {
      setTimeout(() => {
        const el = document.activeElement;
        // FOCUS STILL SETTLING, not left. Between mounting this portaled menu and
        // the visible-effect moving focus into it, the browser parks focus on
        // <body> for a tick. Closing here would kill the editor before it ever
        // took a key — in a grid cell the next ArrowDown/Enter then falls through
        // to cell navigation, or a click never enters edit mode at all
        // (interaction witness, 19 Oct). A real Tab-away always lands on another
        // element, never <body>; a click on empty space is caught by mousedown.
        if (!el || el === document.body) return;
        if (trigger.current?.contains(el) || pop.current?.contains(el)) return;
        reason.current = 'away';
        setOpen(false);
      }, 0);
    };
    document.addEventListener('mousedown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('focusout', onFocusOut);
    return () => {
      document.removeEventListener('mousedown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, [open]);

  const close = (why: 'pick' | 'cancel' | 'next' | 'prev' = 'pick') => {
    reason.current = why;
    setOpen(false);
    refocusTrigger();
  };
  const choose = (v: string, why: 'pick' | 'next' | 'prev' = 'pick') => {
    onChange(v);
    close(why);
  };
  const move = (d: number) =>
    setActive((a) => (options.length ? Math.min(Math.max(0, a + d), options.length - 1) : 0));

  const onListKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Escape') {
      // Swallow it so it closes only the menu, not the card around it.
      e.preventDefault();
      e.stopPropagation();
      close('cancel');
    } else if (e.key === 'Tab' && onClose) {
      // In the grid, Tab commits the highlighted row and steps to the next cell
      // (Shift+Tab the previous one) — the spreadsheet move. preventDefault stops
      // the browser moving focus out from under us; the grid re-places it. On the
      // card (no onClose) Tab is left alone, so it leaves the field as before.
      e.preventDefault();
      e.stopPropagation();
      if (options[active]) choose(options[active].value, e.shiftKey ? 'prev' : 'next');
      else close(e.shiftKey ? 'prev' : 'next');
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      move(1);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      move(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(options.length - 1);
    } else if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      e.stopPropagation();
      if (options[active]) choose(options[active].value);
    } else if (e.key.length === 1 && !e.metaKey && !e.ctrlKey && !e.altKey) {
      // Type-ahead jump, like a native select: a fresh key after a pause starts
      // a new search; quick keys build a prefix.
      const now = Date.now();
      typeBuf.current = now - typeAt.current > 700 ? e.key : typeBuf.current + e.key;
      typeAt.current = now;
      const q = typeBuf.current.toLowerCase();
      const hit = options.findIndex((o) => o.label.toLowerCase().startsWith(q));
      if (hit >= 0) setActive(hit);
    }
  };

  return (
    <span className="area-combo" ref={wrap}>
      <button
        ref={trigger}
        type="button"
        className="ctl quiet area-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={ariaLabel}
        disabled={disabled}
        onClick={() => setOpen((o) => !o)}
        onKeyDown={(e) => {
          if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            setOpen(true);
          }
        }}
      >
        {current?.mark}
        {current?.chipClass ? (
          <span className={`moscow-chip ${current.chipClass}`}>{current.label}</span>
        ) : current?.trailing ? (
          <span className="sr-ta-nameline area-trigger-nameline">
            <span className="area-trigger-name">{current.label}</span>
            {current.trailing}
          </span>
        ) : (
          <span className={`area-trigger-name${current ? '' : ' area-trigger-none'}`}>
            {current ? current.label : options[0]?.label ?? ''}
          </span>
        )}
      </button>

      {open && createPortal(
        <div
          ref={pop}
          className="sr-ta area-pop pick-pop"
          role="listbox"
          aria-label={ariaLabel}
          tabIndex={-1}
          onKeyDown={onListKeyDown}
          style={{
            position: 'fixed',
            top: pos?.top ?? -9999,
            left: pos?.left ?? -9999,
            minWidth: pos?.anchorW,
            maxHeight: pos?.maxH,
            overflowY: 'auto',
            zIndex: 200,
            visibility: pos ? 'visible' : 'hidden',
          }}
        >
          <div className="sr-ta-body">
            {options.map((o, i) => {
              const on = o.value === value;
              const header = !!o.group && (i === 0 || options[i - 1].group !== o.group);
              return (
                <Fragment key={o.value || `__${i}`}>
                  {header && <span className="sr-ta-label">{o.group}</span>}
                  <button
                    type="button"
                    role="option"
                    aria-selected={on}
                    className={`sr-ta-opt pick-opt ${i === active ? 'is-active' : ''} ${on ? 'is-on' : ''}`}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => choose(o.value)}
                  >
                    {o.mark}
                    <span className="sr-ta-opt-text">
                      {o.chipClass ? (
                        <span className={`moscow-chip ${o.chipClass}`}>{o.label}</span>
                      ) : o.trailing ? (
                        <span className="sr-ta-nameline">
                          <span className="sr-ta-name">{o.label}</span>
                          {o.trailing}
                        </span>
                      ) : (
                        <span className="sr-ta-name">{o.label}</span>
                      )}
                      {o.sub && <span className="sr-ta-sub">{o.sub}</span>}
                    </span>
                    {on && (
                      <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
                        <path d="M2.5 7.5 5.5 10.5 11.5 3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </button>
                </Fragment>
              );
            })}
          </div>
        </div>,
        document.body,
      )}
    </span>
  );
}
